import { brandData } from '../data/brand-data.js';

export function renderDemographicsView() {
  const { personas, sites, macroTrends } = brandData;

  return `
    <section class="view-section" id="view-demographics">
      <div class="hero-card">
        <span class="hero-tag">Phase 02 Demographics & Culture</span>
        <h2>Chicago Neighborhood Profiles & Audience Personas</h2>
        <p style="max-width: 800px; font-size: var(--text-lg); margin-bottom: 0;">
          Data-backed intelligence sourced from the <strong>U.S. Census Bureau ACS / CMAP Community Snapshots</strong>, <strong>Pew Research Center</strong>, <strong>PRRI</strong>, and the <strong>U.S. Surgeon General</strong>.
        </p>
      </div>

      <!-- Macro Trends Banner -->
      <h3 style="margin-bottom: var(--space-lg);">Macro Cultural & Faith Dynamics (Cited Benchmarks)</h3>
      <div class="grid-3" style="margin-bottom: var(--space-2xl);">
        ${macroTrends.map(t => `
          <div class="card" style="border-top: 3px solid var(--accent-primary);">
            <div style="color: var(--accent-gold); font-size: var(--text-3xl); font-weight: 800; margin-bottom: 4px;">${t.stat}</div>
            <div style="font-weight: 700; color: var(--text-primary); font-size: var(--text-base); margin-bottom: 2px;">${t.title}</div>
            <div class="mono" style="font-size: var(--text-xs); color: var(--accent-primary); margin-bottom: 8px;">${t.subtitle}</div>
            <p style="font-size: var(--text-xs); margin: 0; line-height: 1.6;">${t.description}</p>
          </div>
        `).join('')}
      </div>

      <!-- 4 Active Campus Demographic Snapshots -->
      <h3 style="margin-bottom: var(--space-lg);">Four Active Campus Demographic Profiles (CMAP / U.S. Census Data)</h3>
      <div class="grid-2" style="margin-bottom: var(--space-2xl);">
        ${sites.map(site => `
          <div class="card">
            <div class="card-header" style="align-items: flex-start;">
              <div>
                <h4 style="font-size: var(--text-xl); color: var(--text-primary); margin: 0;">${site.name}</h4>
                <div style="font-size: var(--text-xs); color: var(--accent-primary); font-weight: 600; margin-top: 2px;">${site.venue}</div>
                <div style="font-size: var(--text-xs); color: var(--text-tertiary);">${site.address}</div>
              </div>
              <span class="hero-tag" style="margin: 0; background: var(--bg-surface-elevated); color: var(--accent-gold);">${site.time}</span>
            </div>

            <p style="font-size: var(--text-xs); color: var(--text-secondary); margin-bottom: var(--space-md); line-height: 1.5;">${site.vibe}</p>

            <div style="display: grid; grid-template-columns: repeat(3, 1fr); gap: 8px; background: var(--bg-surface-elevated); padding: var(--space-md); border-radius: var(--radius-md); border: 1px solid var(--border-subtle);">
              <div>
                <div style="font-size: 10px; color: var(--text-tertiary); text-transform: uppercase;">Population</div>
                <div class="mono" style="font-size: var(--text-sm); font-weight: 700; color: var(--text-primary);">${site.stats.population}</div>
              </div>
              <div>
                <div style="font-size: 10px; color: var(--text-tertiary); text-transform: uppercase;">Median Age</div>
                <div class="mono" style="font-size: var(--text-sm); font-weight: 700; color: var(--text-primary);">${site.stats.medianAge}</div>
              </div>
              <div>
                <div style="font-size: 10px; color: var(--text-tertiary); text-transform: uppercase;">Young Adults (20-39)</div>
                <div class="mono" style="font-size: var(--text-sm); font-weight: 700; color: var(--accent-primary);">${site.stats.youngAdults}</div>
              </div>
              <div>
                <div style="font-size: 10px; color: var(--text-tertiary); text-transform: uppercase;">Bachelor's Degree+</div>
                <div class="mono" style="font-size: var(--text-sm); font-weight: 700; color: var(--text-primary);">${site.stats.bachelorsPlus}</div>
              </div>
              <div>
                <div style="font-size: 10px; color: var(--text-tertiary); text-transform: uppercase;">Median Income</div>
                <div class="mono" style="font-size: var(--text-sm); font-weight: 700; color: var(--accent-emerald);">${site.stats.medianIncome}</div>
              </div>
              <div>
                <div style="font-size: 10px; color: var(--text-tertiary); text-transform: uppercase;">WalkScore Rating</div>
                <div class="mono" style="font-size: var(--text-sm); font-weight: 700; color: var(--accent-gold);">${site.stats.walkScore}</div>
              </div>
            </div>
          </div>
        `).join('')}
      </div>

      <!-- 5 Audience Personas with Full-Spectrum Journeys -->
      <h3 style="margin-bottom: var(--space-lg);">Full-Spectrum Audience Personas & Journey Pathways</h3>
      <div class="grid-2" style="margin-bottom: var(--space-xl);">
        ${personas.map(p => `
          <div class="card" style="display: flex; flex-direction: column; justify-content: space-between;">
            <div>
              <div class="card-header" style="align-items: baseline;">
                <div>
                  <h4 style="margin: 0; color: var(--text-primary);">${p.name}</h4>
                  <div style="font-size: var(--text-xs); color: var(--accent-primary); font-weight: 600;">${p.role}</div>
                </div>
                <span class="mono" style="font-size: 10px; background: var(--bg-surface-elevated); padding: 4px 8px; border-radius: var(--radius-sm); color: var(--text-tertiary);">
                  ${p.location}
                </span>
              </div>
              <div class="mono" style="font-size: var(--text-xs); color: var(--accent-gold); margin-bottom: 8px;">
                Anchor: ${p.site}
              </div>
              <p style="font-size: var(--text-xs); line-height: 1.6; margin-bottom: var(--space-md);">
                ${p.bio}
              </p>
            </div>
            
            <div style="background: var(--bg-surface-elevated); padding: var(--space-sm) var(--space-md); border-radius: var(--radius-md); border-left: 3px solid var(--accent-primary);">
              <div style="font-size: 10px; font-weight: 700; color: var(--text-primary); text-transform: uppercase; margin-bottom: 2px;">First-Visit Pathway:</div>
              <p style="font-size: 11px; color: var(--text-secondary); margin: 0; line-height: 1.4;">${p.pathway}</p>
            </div>
          </div>
        `).join('')}
      </div>

      <div class="card" style="background: rgba(99, 102, 241, 0.05); border: 1px solid var(--border-accent);">
        <div style="font-weight: 700; color: var(--text-primary); margin-bottom: 4px;">Data Verification & Citations</div>
        <p style="font-size: var(--text-xs); color: var(--text-secondary); margin: 0; line-height: 1.6;">
          All neighborhood demographics are sourced from the <strong>Chicago Metropolitan Agency for Planning (CMAP) Community Data Snapshots</strong> (U.S. Census Bureau ACS 5-Year Estimates). Religious trends and LGBTQ+ attitudes are sourced from the <strong>Pew Research Center Religious Landscape Study</strong> and the <strong>Public Religion Research Institute (PRRI) American Values Atlas</strong>.
        </p>
      </div>
    </section>
  `;
}
